import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Text,
  Button,
  Group,
  Badge,
  ActionIcon,
  Menu,
  Modal,
  TextInput,
  NumberInput,
  Select,
  Switch,
  Stack,
  LoadingOverlay,
  Image,
  ScrollArea,
  Divider,
  Avatar,
  ThemeIcon,
  Center,
  Card,
  Tooltip,
  SegmentedControl,
  RingProgress,
  Removable,
} from "@mantine/core";
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconPhoto,
  IconClick,
  IconEye,
  IconCheck,
  IconLink,
  IconDeviceMobile,
  IconX,
  IconSearch,
  IconArrowRight,
} from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { ImageUploadComponent } from "../projctManagement/adminProjectManagement";
import { API_BASE_URL as ROOT_API_BASE_URL } from "../../config/baseUrls";
// --- AUTH & API ---
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

const API_BASE_URL = `${ROOT_API_BASE_URL}/admin`;

// --- COMPONENT: LIVE MOBILE PREVIEW (Used in Modal) ---
const LiveMobilePreview = ({ bannerData }) => {
  return (
    <Center h="100%">
      <Box
        w={280}
        h={560}
        bg="black"
        style={{
          borderRadius: "40px",
          border: "10px solid #1f1f1f",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 20px 50px -10px rgba(0,0,0,0.3)",
        }}
      >
        {/* Phone UI: Status Bar */}
        <Group
          justify="space-between"
          px="md"
          pt="sm"
          className="absolute top-0 w-full z-20"
        >
          <Text size="10px" fw={700} c="white">
            9:41
          </Text>
          <Group gap={2}>
            <Box
              w={12}
              h={12}
              bg="white"
              style={{ borderRadius: "50%" }}
              opacity={0.8}
            />
            <Box
              w={12}
              h={6}
              bg="white"
              style={{ borderRadius: 2 }}
              opacity={0.8}
            />
          </Group>
        </Group>

        {/* Phone UI: App Content Mockup */}
        <Box bg="#f8f9fa" w="100%" h="100%" pt={40} px="md">
          {/* Mock Header */}
          <Group justify="space-between" mb="md">
            <Avatar size="sm" src={null} />
            <Box w={80} h={8} bg="gray.3" style={{ borderRadius: 4 }} />
            <IconSearch size={16} color="gray" />
          </Group>

          <Text size="xs" fw={700} mb="xs" c="dimmed">
            FEATURED
          </Text>

          {/* THE BANNER PREVIEW */}
          <Paper
            radius="lg"
            className="overflow-hidden relative shadow-md"
            style={{ aspectRatio: "9/10", width: "100%" }}
            bg="white"
          >
            {bannerData.imageUrl ? (
              <Image
                src={bannerData.imageUrl}
                h="100%"
                w="100%"
                fit="cover"
                alt="Preview"
              />
            ) : (
              <Center h="100%" bg="gray.1">
                <Stack align="center" gap={4}>
                  <IconPhoto size={32} className="text-gray-300" />
                  <Text size="xs" c="dimmed">
                    Image Preview
                  </Text>
                </Stack>
              </Center>
            )}

            {/* Simulated Interaction Hint */}
            {bannerData.navigateTo !== "none" && (
              <Box className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full">
                <Group gap={4}>
                  <Text size="8px" fw={700} c="white" tt="uppercase">
                    {bannerData.navigateTo === "Voting"
                      ? "Vote Now"
                      : "Shop Now"}
                  </Text>
                  <IconArrowRight size={8} color="white" />
                </Group>
              </Box>
            )}
          </Paper>

          {/* Mock Feed Content Below */}
          <Stack mt="xl" gap="md" opacity={0.4}>
            <Box w="60%" h={12} bg="gray.3" style={{ borderRadius: 4 }} />
            <Group>
              <Box w={40} h={40} bg="gray.3" style={{ borderRadius: 8 }} />
              <Stack gap={4} style={{ flex: 1 }}>
                <Box w="80%" h={8} bg="gray.3" style={{ borderRadius: 4 }} />
                <Box w="40%" h={8} bg="gray.3" style={{ borderRadius: 4 }} />
              </Stack>
            </Group>
          </Stack>
        </Box>
      </Box>
    </Center>
  );
};

// --- COMPONENT: BANNER FORM MODAL ---
const BannerFormModal = ({ isOpen, onClose, banner, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  // FORM STATE
  const [formData, setFormData] = useState({
    title: "",
    imageUrl: "",
    isActive: true,
    displayOrder: 0,
    navigateTo: "none",
    selectedItem: null,
  });

  // ITEM SELECTION STATE
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsPagination, setItemsPagination] = useState({
    page: 1,
    hasNext: false,
  });

  const { showSuccess, showError } = useNotification();

  // RESET FORM ON OPEN
  useEffect(() => {
    if (isOpen) {
      if (banner) {
        // Edit Mode: Populate Data
        let navigateTo = "none";
        let selectedItem = null;
        if (banner.metadata?.projectId) {
          navigateTo = "Voting";
          selectedItem = banner.metadata;
        } else if (banner.metadata?.productId) {
          navigateTo = "Purchase";
          selectedItem = banner.metadata;
        }

        setFormData({
          title: banner.title || "",
          imageUrl: banner.imageUrl || "",
          isActive: banner.isActive ?? true,
          displayOrder: banner.displayOrder || 0,
          navigateTo,
          selectedItem,
        });
      } else {
        // Create Mode: STRICT RESET
        setFormData({
          title: "",
          imageUrl: "",
          isActive: true,
          displayOrder: 0,
          navigateTo: "none",
          selectedItem: null,
        });
        setItems([]);
      }
    }
  }, [isOpen, banner]);

  // FETCH ITEMS (Projects/Products)
  useEffect(() => {
    if (formData.navigateTo !== "none" && isOpen) fetchItems(1);
    else {
      setItems([]);
    }
  }, [formData.navigateTo]);

  const fetchItems = async (page = 1) => {
    setItemsLoading(true);
    try {
      const endpoint =
        formData.navigateTo === "Voting"
          ? `${API_BASE_URL}/admin/projects/pending?status=ACTIVE&page=${page}&limit=20`
          : `${API_BASE_URL}/admin/products?status=AVAILABLE&page=${page}&limit=20`;

      const res = await fetch(endpoint, { headers: getAuthHeaders() });
      const data = await res.json();

      if (data.success) {
        const newItems =
          formData.navigateTo === "Voting"
            ? data.data.projects
            : data.data.products;
        setItems((prev) => (page === 1 ? newItems : [...prev, ...newItems]));
        setItemsPagination(data.data.pagination);
      }
    } catch {
      showError("Error", "Could not load items");
    } finally {
      setItemsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim())
      return showError("Missing Title", "Please name this banner");
    if (!formData.imageUrl.trim())
      return showError("Missing Image", "Please upload a banner image");
    if (formData.navigateTo !== "none" && !formData.selectedItem)
      return showError(
        "Incomplete Link",
        "Please select a Project or Product to link",
      );

    setLoading(true);
    try {
      const endpoint = banner
        ? `${API_BASE_URL}/updateGlobalBanner/${banner.id}`
        : `${API_BASE_URL}/createGlobalBanner`;

      const payload = {
        title: formData.title,
        imageUrl: formData.imageUrl,
        isActive: formData.isActive,
        displayOrder: Number(formData.displayOrder),
        metadata: formData.navigateTo !== "none" ? formData.selectedItem : null,
      };

      const res = await fetch(endpoint, {
        method: banner ? "PUT" : "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.status === true) {
        showSuccess("Success", banner ? "Banner updated" : "Banner created");
        onSuccess();
        onClose();
      } else {
        showError("Error", data.message || "Failed to save");
      }
    } catch {
      showError("Error", "Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (item) => {
    const isProject = formData.navigateTo === "Voting";
    const newItem = {
      [isProject ? "projectId" : "productId"]: item.id,
      isCosplayEvent: item.isCosplayEvent,
      isMerch: isProject
        ? item.projectType === "NEW_PRODUCT"
        : item.productType === "NEW_PRODUCT",
      _tempTitle: item.title, // Store title temporarily for display in UI
      _tempImage: item.primaryImage,
    };
    setFormData({ ...formData, selectedItem: newItem });
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          {banner ? "Edit Banner" : "Create New Banner"}
        </Text>
      }
      size="85%"
      padding={0}
      radius="lg"
      styles={{ body: { height: "650px", display: "flex" } }}
    >
      <Grid w="100%" h="100%" gutter={0}>
        {/* LEFT: CONFIG FORM */}
        <Grid.Col
          span={7}
          p="xl"
          style={{ borderRight: "1px solid #eee", overflowY: "auto" }}
        >
          <Stack gap="lg">
            <LoadingOverlay visible={loading} />

            <Group grow align="start">
              <TextInput
                label="Banner Internal Title"
                placeholder="e.g. Summer Sale Promo"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
              <NumberInput
                label="Sort Order (0 = First Slide)"
                description=""
                min={0}
                max={100}
                value={formData.displayOrder}
                onChange={(v) => setFormData({ ...formData, displayOrder: v })}
              />
            </Group>

            <Box>
              <Text size="sm" fw={500} mb={4}>
                Banner Image (9:10 Ratio){" "}
                <span style={{ color: "red" }}>*</span>
              </Text>
              <ImageUploadComponent
                onUpload={(url) => setFormData({ ...formData, imageUrl: url })}
                existingUrl={formData.imageUrl}
              />
            </Box>

            <Divider label="Interaction" labelPosition="left" />

            <Stack gap="xs">
              <Text size="sm" fw={500}>
                On Tap Action
              </Text>
              <SegmentedControl
                fullWidth
                value={formData.navigateTo}
                onChange={(v) =>
                  setFormData({
                    ...formData,
                    navigateTo: v,
                    selectedItem: null,
                  })
                }
                data={[
                  { label: "No Action", value: "none" },
                  { label: "Open Project (Vote)", value: "Voting" },
                  { label: "Open Store (Buy)", value: "Purchase" },
                ]}
              />
            </Stack>

            {/* DYNAMIC ITEM SELECTOR */}
            {formData.navigateTo !== "none" && (
              <Box className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                {formData.selectedItem ? (
                  <Group
                    justify="space-between"
                    bg="white"
                    p="sm"
                    className="border rounded-md shadow-sm"
                  >
                    <Group>
                      <ThemeIcon
                        color={
                          formData.navigateTo === "Voting" ? "blue" : "purple"
                        }
                        variant="light"
                      >
                        <IconCheck size={16} />
                      </ThemeIcon>
                      <Box>
                        <Text size="sm" fw={600}>
                          Item Linked Successfully
                        </Text>
                        <Text
                          size="xs"
                          c="dimmed"
                          style={{ fontFamily: "monospace" }}
                        >
                          ID:{" "}
                          {formData.selectedItem.projectId ||
                            formData.selectedItem.productId}
                        </Text>
                      </Box>
                    </Group>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() =>
                        setFormData({ ...formData, selectedItem: null })
                      }
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  </Group>
                ) : (
                  <Stack gap="xs">
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                      Select a{" "}
                      {formData.navigateTo === "Voting" ? "Project" : "Product"}
                    </Text>
                    <ScrollArea
                      h={180}
                      className="bg-white rounded border border-gray-200"
                    >
                      {itemsLoading && items.length === 0 ? (
                        <Center h="100%">
                          <Text size="sm" c="dimmed">
                            Loading...
                          </Text>
                        </Center>
                      ) : (
                        <Stack gap={0}>
                          {items.map((item) => (
                            <Group
                              key={item.id}
                              className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-0"
                              onClick={() => handleSelectItem(item)}
                              wrap="nowrap"
                            >
                              <Avatar
                                src={item.primaryImage}
                                size="sm"
                                radius="sm"
                              />
                              <Text size="sm" lineClamp={1} style={{ flex: 1 }}>
                                {item.title}
                              </Text>
                              <Badge size="xs" variant="outline">
                                {item.projectType || item.productType}
                              </Badge>
                            </Group>
                          ))}
                          {itemsPagination.hasNext && (
                            <Button
                              variant="subtle"
                              fullWidth
                              size="xs"
                              onClick={() =>
                                fetchItems(itemsPagination.page + 1)
                              }
                            >
                              Load More
                            </Button>
                          )}
                        </Stack>
                      )}
                    </ScrollArea>
                  </Stack>
                )}
              </Box>
            )}

            <Group justify="space-between" mt="auto" pt="lg">
              <Switch
                label="Set Banner Active"
                size="md"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    isActive: e.currentTarget.checked,
                  })
                }
              />
              <Group>
                <Button variant="subtle" color="gray" onClick={onClose}>
                  Discard
                </Button>
                <Button
                  onClick={handleSubmit}
                  color="blue"
                  leftSection={<IconCheck size={16} />}
                >
                  Save Changes
                </Button>
              </Group>
            </Group>
          </Stack>
        </Grid.Col>

        {/* RIGHT: LIVE PREVIEW */}
        <Grid.Col
          span={5}
          bg="gray.0"
          p="xl"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text fw={700} c="dimmed" tt="uppercase" size="xs" lts={2} mb="lg">
            Live Preview
          </Text>
          <LiveMobilePreview bannerData={formData} />
          <Text size="xs" c="dimmed" mt="lg" ta="center" maw={200}>
            This is how your banner will appear on the user's home feed.
          </Text>
        </Grid.Col>
      </Grid>
    </Modal>
  );
};

// --- MAIN PAGE ---
export default function GlobalBannerManagement() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const { showSuccess, showError } = useNotification();

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/globalBanners`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (Array.isArray(data)) setBanners(data);
    } catch {
      showError("Error", "Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleToggle = async (banner) => {
    // Optimistic Update
    const oldBanners = [...banners];
    setBanners(
      banners.map((b) =>
        b.id === banner.id ? { ...b, isActive: !b.isActive } : b,
      ),
    );
    try {
      const res = await fetch(
        `${API_BASE_URL}/toggleGlobalBanner/${banner.id}`,
        { method: "PUT", headers: getAuthHeaders() },
      );
      const data = await res.json();
      if (data.status === 200) showSuccess("Updated", "Banner status changed");
      else throw new Error();
    } catch {
      setBanners(oldBanners); // Revert
      showError("Error", "Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Permanently delete this banner?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/deleteGlobalBanner/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.status === true) {
        showSuccess("Deleted", "Banner removed");
        fetchBanners();
      }
    } catch {
      showError("Error", "Delete failed");
    }
  };

  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            Global Banners
          </Text>
          <Text size="sm" c="dimmed">
            Manage home screen carousel slides
          </Text>
        </Box>
        <Button
          leftSection={<IconPlus size={18} />}
          className="btn-ota-primary"
          onClick={() => {
            setSelectedBanner(null);
            setShowModal(true);
          }}
        >
          New Banner
        </Button>
      </Group>

      {loading ? (
        <Center h={200}>
          <Stack align="center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <Text c="dimmed">Loading...</Text>
          </Stack>
        </Center>
      ) : banners.length === 0 ? (
        <Paper withBorder p="xl" ta="center" bg="gray.0" radius="md">
          <ThemeIcon size={60} radius="xl" color="gray" variant="light" mb="md">
            <IconPhoto size={30} />
          </ThemeIcon>
          <Text fw={600} size="lg">
            No Banners Yet
          </Text>
          <Text c="dimmed" size="sm" mb="md">
            Create your first banner to feature content on the mobile app.
          </Text>
          <Button
            variant="white"
            onClick={() => {
              setSelectedBanner(null);
              setShowModal(true);
            }}
          >
            Create Banner
          </Button>
        </Paper>
      ) : (
        <Grid>
          {banners.map((banner) => (
            <Grid.Col key={banner.id} span={{ base: 12, sm: 6, lg: 4, xl: 3 }}>
              <Card
                withBorder
                radius="md"
                padding="0"
                className="overflow-visible shadow-sm hover:shadow-md transition-shadow h-full flex flex-col"
              >
                {/* Visual Header */}
                <Box
                  className="relative bg-gray-100 rounded-t-md overflow-hidden"
                  style={{ aspectRatio: "9/10" }}
                >
                  <Image src={banner.imageUrl} h="100%" w="100%" fit="cover" />

                  {/* Floating Actions */}
                  <Group className="absolute top-2 right-2" gap={4}>
                    <Menu position="bottom-end" shadow="md">
                      <Menu.Target>
                        <ActionIcon
                          variant="white"
                          radius="xl"
                          size="lg"
                          className="shadow-sm opacity-90 hover:opacity-100"
                        >
                          <IconDotsVertical size={18} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEdit size={14} />}
                          onClick={() => {
                            setSelectedBanner(banner);
                            setShowModal(true);
                          }}
                        >
                          Edit
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>

                  <Badge
                    className="absolute top-2 left-2 shadow-sm bg-black/50 backdrop-blur-md text-white"
                    radius="sm"
                  >
                    Order: {banner.displayOrder}
                  </Badge>
                </Box>

                {/* Content Body */}
                <Stack p="md" gap="sm" style={{ flex: 1 }}>
                  <Group justify="space-between" align="start" wrap="nowrap">
                    <Text
                      fw={600}
                      size="md"
                      lineClamp={2}
                      title={banner.title}
                      lh={1.2}
                    >
                      {banner.title}
                    </Text>
                    <Tooltip
                      label={banner.isActive ? "Deactivate" : "Activate"}
                    >
                      <Switch
                        checked={banner.isActive}
                        onChange={() => handleToggle(banner)}
                        color="green"
                        size="md"
                        thumbIcon={
                          banner.isActive ? (
                            <IconCheck size={10} color="green" />
                          ) : null
                        }
                      />
                    </Tooltip>
                  </Group>

                  {/* Smart Metadata Display */}
                  <Box mt="auto">
                    {banner.metadata?.projectId ? (
                      <Badge
                        variant="light"
                        color="blue"
                        fullWidth
                        leftSection={<IconLink size={12} />}
                        radius="sm"
                      >
                        Opens Project
                      </Badge>
                    ) : banner.metadata?.productId ? (
                      <Badge
                        variant="light"
                        color="purple"
                        fullWidth
                        leftSection={<IconLink size={12} />}
                        radius="sm"
                      >
                        Opens Product
                      </Badge>
                    ) : (
                      <Badge variant="light" color="gray" fullWidth radius="sm">
                        Static Banner
                      </Badge>
                    )}
                  </Box>
                </Stack>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      )}

      {/* MODAL INJECTION */}
      <BannerFormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        banner={selectedBanner}
        onSuccess={fetchBanners}
      />
    </Box>
  );
}
